import {
  Box,
  Button,
  Card,
  Loader,
  Menu,
  Stack,
  Tabs,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { AppPage } from '@markku/components'
import {
  IconBuilding,
  IconCalendar,
  IconChartBar,
  IconChevronDown,
  IconHome,
  IconPlus,
} from '@tabler/icons-react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { AddPropertyModal } from '../../components/profile/AddPropertyModal'
import { usePropertyCreation } from '../../lib/hooks/usePropertyCreation'
import { propertyHooks } from '../../lib/trpcHooks'
import { Summary } from '../../modules/maintenance/Summary'
import { YearCalendarCarousel } from '../../modules/maintenance/YearCalendarCarousel'

function MaintenancePage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/_app/maintenance' })
  const { handlePropertyCreated, WelcomeModal } = usePropertyCreation()
  const [
    addFirstPropertyOpened,
    { open: openAddFirstProperty, close: closeAddFirstProperty },
  ] = useDisclosure(false)

  const handlePropertyCreatedAndCloseModal = (
    propertyId: number,
    propertyAddress: string,
  ) => {
    closeAddFirstProperty()
    handlePropertyCreated(propertyId, propertyAddress)
  }

  // Fetch user's properties
  const { data: propertiesData, isLoading } = propertyHooks.useList()
  const properties = propertiesData?.data

  // Get active tab and selected propertyId from URL search params
  const activeTab = (search as any).tab || 'yearCalendar'
  const selectedPropertyId = (search as any).propertyId

  // Default to first property if none selected
  const propertyId =
    selectedPropertyId && properties?.length
      ? Number(selectedPropertyId)
      : properties?.[0]?.id

  // Handle tab change by updating URL with replace to avoid adding history entries
  const handleTabChange = (value: string | null) => {
    if (value) {
      navigate({
        search: (prev: any) => ({ ...prev, tab: value }),
        replace: true,
      } as any)
    }
  }

  // Handle property change
  const handlePropertyChange = (value: string | null) => {
    if (value) {
      navigate({
        search: (prev: any) => ({ ...prev, propertyId: Number(value) }),
        replace: true,
      } as any)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <AppPage
        title="Huoltokirja"
        subtitle="Seuranta ja yhteenveto"
        backTarget="/home"
      >
        <Stack align="center" justify="center" h={200}>
          <Loader size="lg" />
        </Stack>
      </AppPage>
    )
  }

  // No properties found
  if (!properties?.length) {
    return (
      <AppPage
        title="Huoltokirja"
        subtitle="Seuranta ja yhteenveto"
        backTarget="/home"
      >
        <Card
          radius="md"
          withBorder
          onClick={openAddFirstProperty}
          style={{
            cursor: 'pointer',
            borderColor: 'var(--mantine-color-brandOrange-2)',
            transition: 'all 0.2s',
          }}
          styles={{
            root: {
              '&:hover': {
                borderColor: 'var(--mantine-color-brandOrange-4)',
                transform: 'translateY(-2px)',
              },
            },
          }}
        >
          <Stack gap="md" align="center" py="xl">
            <Box
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--mantine-color-brandOrange-0)',
              }}
            >
              <IconHome size={30} color="var(--mantine-color-brandOrange-6)" />
            </Box>
            <Stack gap={4} align="center">
              <Text fw={600} size="lg">
                Lisää ensimmäinen kiinteistösi
              </Text>
              <Text c="dimmed" ta="center">
                Aloita lisäämällä kiinteistö huoltokirjaa varten
              </Text>
            </Stack>
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              color="brandOrange"
            >
              Lisää kiinteistö
            </Button>
          </Stack>
        </Card>

        <AddPropertyModal
          opened={addFirstPropertyOpened}
          onClose={closeAddFirstProperty}
          onPropertyCreated={handlePropertyCreatedAndCloseModal}
        />

        {WelcomeModal}
      </AppPage>
    )
  }

  // Get selected property
  const selectedProperty = properties.find((p) => p.id === propertyId)
  const propertyAddress = selectedProperty?.address
    ? `${selectedProperty.address.street}, ${selectedProperty.address.city}`
    : 'Kiinteistö'

  // Property selector for multi-property users - grouped by customer
  const propertyOptions = properties.map((p) => ({
    value: p.id.toString(),
    label: p.address?.street || `Kiinteistö ${p.id}`,
    street: p.address?.street || `Kiinteistö ${p.id}`,
    city: p.address?.city,
    customerName: p.customer?.name || 'Ei asiakkuutta',
  }))

  // Group properties by customer
  const propertiesByCustomer = propertyOptions.reduce(
    (acc, prop) => {
      const customerName = prop.customerName
      if (!acc[customerName]) {
        acc[customerName] = []
      }
      acc[customerName].push(prop)
      return acc
    },
    {} as Record<string, typeof propertyOptions>,
  )

  const selectedOption = propertyOptions.find(
    (o) => o.value === propertyId?.toString(),
  )

  const propertySelector =
    properties.length > 1 ? (
      <Menu position="bottom-end" width={250}>
        <Menu.Target>
          <UnstyledButton
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Stack gap={2} align="flex-end" style={{ flex: 1 }}>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {selectedOption?.street || 'Kiinteistö'}
                </Text>
              </Box>
              {selectedOption?.city && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {selectedOption.city}
                </Text>
              )}
            </Stack>
            <IconChevronDown
              size={12}
              color="var(--mantine-color-gray-6)"
              style={{ flexShrink: 0 }}
            />
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          {Object.entries(propertiesByCustomer).map(
            ([customerName, customerProperties], groupIndex) => (
              <div key={customerName}>
                {groupIndex > 0 && <Menu.Divider />}
                <Menu.Label>{customerName}</Menu.Label>
                {customerProperties.map((opt) => (
                  <Menu.Item
                    key={opt.value}
                    onClick={() => handlePropertyChange(opt.value)}
                  >
                    <Stack gap={0}>
                      <Text size="sm">{opt.street}</Text>
                      {opt.city && (
                        <Text size="xs" c="dimmed">
                          {opt.city}
                        </Text>
                      )}
                    </Stack>
                  </Menu.Item>
                ))}
              </div>
            ),
          )}
        </Menu.Dropdown>
      </Menu>
    ) : undefined

  return (
    <>
      <AppPage
        title="Huoltokirja"
        subtitle={properties.length === 1 ? propertyAddress : undefined}
        backTarget="/home"
        rightSection={propertySelector}
      >
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List grow>
            <Tabs.Tab
              value="yearCalendar"
              leftSection={<IconCalendar size={16} />}
            >
              Vuosikello
            </Tabs.Tab>
            <Tabs.Tab value="summary" leftSection={<IconChartBar size={16} />}>
              Kiinteistö
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="yearCalendar">
            <Box mt="md">
              {propertyId && <YearCalendarCarousel propertyId={propertyId} />}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="summary">
            <Box mt="md">
              {propertyId && <Summary propertyId={propertyId} />}
            </Box>
          </Tabs.Panel>
        </Tabs>
      </AppPage>
    </>
  )
}

export const Route = createFileRoute('/_app/maintenance')({
  component: MaintenancePage,
})
